import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { Button } from '@praktio/common-ui';
import { Dispatch, SetStateAction, useState } from 'react';

import { insertNumbering } from '../../../utils/slide';
import { getPDF } from '../../../ducks/learnerReducer';
import { SupplementalDocStates } from '../../../utils/constants';
import { wrapElementsInAnnotationSpan } from '../../../utils/annotations';

import './SupplementalDocument.scss';

interface IProps {
  supplementalDoc?: SupplementalDoc;
  learnerId?: string;
  embedded?: boolean;
  supplementalDocState?: SupplementalDocStates;
  mainDocIsLandscape: boolean;
  setSupplementalDocState?: Dispatch<SetStateAction<SupplementalDocStates>>;
}

const SupplementalDocument = ({
  supplementalDoc,
  learnerId,
  embedded,
  supplementalDocState,
  mainDocIsLandscape,
  setSupplementalDocState,
}: IProps) => {
  const dispatch = useDispatch();
  const [downloadButtonDisabled, setDownloadButtonDisabled] = useState(false);

  if (!supplementalDoc?.content) {
    return null;
  }

  return (
    <div
      className={classNames('SupplementalDocument-wrapper', supplementalDocState, {
        embedded,
        'd-flex d-lg-none': embedded,
        'd-none d-lg-flex first-to-shrink': !embedded,
        'main-doc-is-landscape': mainDocIsLandscape,
      })}
    >
      {embedded && (
        <div className="SupplementalDocument-menu">
          <div>Secondary Document</div>
          <div className="SupplementalDocument-menu-controls">
            <span
              className="material-icons"
              onClick={() => {
                if (setSupplementalDocState && supplementalDocState) {
                  if (supplementalDocState === SupplementalDocStates.Closed) {
                    setSupplementalDocState(SupplementalDocStates.HalfOpen);
                  }
                  if (supplementalDocState === SupplementalDocStates.HalfOpen) {
                    setSupplementalDocState(SupplementalDocStates.FullyOpen);
                  }
                  if (supplementalDocState === SupplementalDocStates.FullyOpen) {
                    setSupplementalDocState(SupplementalDocStates.HalfOpen);
                  }
                }
              }}
            >
              {supplementalDocState &&
              [SupplementalDocStates.Closed, SupplementalDocStates.HalfOpen].includes(
                supplementalDocState,
              )
                ? 'expand_less'
                : 'expand_more'}
            </span>
            {supplementalDocState !== SupplementalDocStates.Closed && (
              <span
                className="material-icons"
                onClick={() => {
                  if (setSupplementalDocState) {
                    setSupplementalDocState(SupplementalDocStates.Closed);
                  }
                }}
              >
                close
              </span>
            )}
          </div>
        </div>
      )}
      <div
        tabIndex={0}
        role="region"
        aria-label="Supplemental Document - Scrollable content area"
        className={classNames('SupplementalDocument', { embedded })}
      >
        <div
          className={classNames('document-html', supplementalDoc?.baseClass)}
          dangerouslySetInnerHTML={{
            __html: insertNumbering(
              wrapElementsInAnnotationSpan(supplementalDoc.content),
              supplementalDoc.outlineTemplate,
            ),
          }}
        ></div>
      </div>
      {supplementalDocState !== SupplementalDocStates.Closed && (
        <Button
          className="download-button"
          onClick={() => {
            if (learnerId) {
              setDownloadButtonDisabled(true);
              dispatch(getPDF(learnerId, supplementalDoc) as any).then(() => {
                setDownloadButtonDisabled(false);
              });
            }
          }}
          tooltip="Download"
          tooltipPlacement="right"
          aria-label="Download supplemental document as PDF"
          aria-pressed={downloadButtonDisabled}
          disabled={downloadButtonDisabled}
          showSpinner={downloadButtonDisabled}
          overlaySpinner
        >
          <span className="material-icons" aria-hidden="true">
            save_alt
          </span>
        </Button>
      )}
    </div>
  );
};

export default SupplementalDocument;
